// BI extras — API hooks for COPQ, FX rates, plan-alert subscriptions.
import axios from "axios";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const buildParams = (filters = {}) => {
  const p = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => {
    if (v == null || v === "" || (Array.isArray(v) && v.length === 0)) return;
    if (Array.isArray(v)) p.set(k, v.join(","));
    else p.set(k, String(v));
  });
  const s = p.toString();
  return s ? `?${s}` : "";
};

// ─── COPQ ──────────────────────────────────────────────────────────────────

export const useBICopq = (filters) =>
  useQuery({
    queryKey: ["bi-copq", filters],
    queryFn: () => axios.get(`${API}/bi/copq${buildParams(filters)}`).then((r) => r.data),
    staleTime: 30_000,
    keepPreviousData: true,
  });

// ─── Phase J.3 · Contract-vs-Spot ──────────────────────────────────────────

export const useBIContractVsSpot = (filters) =>
  useQuery({
    queryKey: ["bi-contract-vs-spot", filters],
    queryFn: () =>
      axios.get(`${API}/bi/contract-vs-spot${buildParams(filters)}`).then((r) => r.data),
    staleTime: 30_000,
    keepPreviousData: true,
  });

// ─── FX rates ──────────────────────────────────────────────────────────────

export const useFxRates = (year) =>
  useQuery({
    queryKey: ["bi-fx-rates", year],
    queryFn: () => axios.get(`${API}/bi/fx-rates?year=${year}`).then((r) => r.data),
  });

export const useFxBulkUpsert = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => axios.post(`${API}/bi/fx-rates/bulk`, body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["bi-fx-rates"] }),
  });
};

// ─── Plan-alert subscriptions ──────────────────────────────────────────────

export const useAlertSettings = () =>
  useQuery({
    queryKey: ["bi-plan-alert-settings"],
    queryFn: () => axios.get(`${API}/bi/plan-alerts/settings`).then((r) => r.data),
  });

export const useUpsertAlertSettings = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => axios.post(`${API}/bi/plan-alerts/settings`, body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["bi-plan-alert-settings"] }),
  });
};

export const useAlertSubs = () =>
  useQuery({
    queryKey: ["bi-plan-alert-subs"],
    queryFn: () => axios.get(`${API}/bi/plan-alerts/subscriptions`).then((r) => r.data),
  });

export const useCreateAlertSub = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => axios.post(`${API}/bi/plan-alerts/subscriptions`, body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["bi-plan-alert-subs"] }),
  });
};

export const usePatchAlertSub = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sid, patch }) =>
      axios.patch(`${API}/bi/plan-alerts/subscriptions/${sid}`, patch).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["bi-plan-alert-subs"] }),
  });
};

export const useDeleteAlertSub = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sid) =>
      axios.delete(`${API}/bi/plan-alerts/subscriptions/${sid}`).then(() => sid),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["bi-plan-alert-subs"] }),
  });
};

export const useScanAlertsNow = () =>
  useMutation({
    mutationFn: () => axios.post(`${API}/bi/plan-alerts/scan-now`, {}).then((r) => r.data),
  });
